import BaseSetup from '@/components/BaseSetup';
import SelfEntity from '@/entity/SelfEntity';
import useMembershipService from '@/services/MembershipService';
import { Reactive } from '@/utils/decorators';
import { Bus, useAside, useLoader, useModal, useNotif } from '@orion.ui/orion';
import { defineAsyncComponent, ref, watch } from 'vue';
// import OnboardingTempWorkerSignedUpModal from '@/components/onboarding/OnboardingTempWorkerSignedUpModal.vue';

import OrganizationRoleListItemEntity from '@/entity/OrganizationRoleListItemEntity';
import useBusService from '@/services/BusService';
import useOrganizationRoleService from '@/services/organization/OrganizationRoleService';
import useTagUserService from '@/services/tag/TagUserService';
import useUserNewsService from '@/services/UserNewsService';
import useZendeskService from '@/services/ZendeskService';
import Broadcast from '@/signalr/Broadcast.signalr';
import { SETTING_TYPE } from '@/typings/enums/SETTING_TYPE';

import { useI18nService } from '@/services/I18nService';
import { SUITE_URL } from '@/utils/CONST';

export type SharedLayoutEmits = {};
export type SharedLayoutProps = {};

export default class SharedLayoutSetup extends BaseSetup {

	static readonly defaultProps = {};

	@Reactive private readonly state = {
		scrollDirection: 'down' as 'down' | 'up',
		unreadNewsCount: 0,
		roles: [] as OrganizationRoleListItemEntity[],
		tags: [] as ITagUserSelfModel[],
		loading: false,
		showPermissionTooltip: false,
		locale: useI18nService().getLocale().value,
	};

	readonly _navigationLoader = ref<OrionLoader>();
	readonly _armNav = ref<HTMLElement>();
	readonly _asideMobileNav = ref<OrionAside>();
	readonly localeOptions = useI18nService().localeOptions;

	private notif: { [id: number]: OrionNotif } = {};

	get scrollDirection () { return this.state.scrollDirection }
	get unreadNewsCount () { return this.state.unreadNewsCount < 0 ? 0 : this.state.unreadNewsCount }
	get roles () { return this.state.roles }
	get tags () { return this.state.tags }
	get loading () { return this.state.loading }

	get selectedLocale () { return this.state.locale }
	set selectedLocale (val: SupportedLocale) {
		this.state.locale = val;
		useI18nService().setLocale(val, true);
	}

	get showPermissionTooltip () { return this.state.showPermissionTooltip }
	set showPermissionTooltip (val) { this.state.showPermissionTooltip = val }

	constructor (
		private props: SharedLayoutProps,
		private emits: SharedLayoutEmits,
	) {
		super();

		this.initAppAsync();

		Bus.on('navigationLoader:show', message => this._navigationLoader.value?.show(message as string));
		Bus.on('navigationLoader:hide', () => this._navigationLoader.value?.hide());

		this.registerBusEvent('toggleLeftAside', (val?: boolean) => this.toggleLeftAside(val));
		this.registerBusEvent('toggleRightAside', (val?: boolean) => this.toggleRightAside(val));
		this.registerBusEvent('openAsideMobileNav', (val: boolean) => {
			val ? this._asideMobileNav.value?.open() : this._asideMobileNav.value?.close();
		});

		useBusService().on('userNews:read', (ids: number[]) => this.state.unreadNewsCount -= ids.length);

		watch([
			() => this.route.name,
			() => this.route.params,
		],
		() => {
			this._asideMobileNav.value?.close();

			if (this.ui.onLayoutNoAside || this.ui.onLayoutOneAside) {
				if (this.route.name !== 'TimeTicketWeek') {
					this.ui.asideLeftSlideOut = false;
					this.ui.asideRightSlideOut = false;
				}
			}
		});
	}

	protected onUnmounted (): void {
		Broadcast.offAddOrUpdate(this.showAdminNotification.bind(this));
		Broadcast.offDelete(this.deleteAdminNotification.bind(this));
	}

	private async initAppAsync () {
		useLoader().show(this.$t('common.loadingYourAccount'));

		Broadcast.onAddOrUpdate(this.showAdminNotification.bind(this));
		Broadcast.onDelete(this.deleteAdminNotification.bind(this));

		await SelfEntity.getUserInfosAsync();
		await this.checkReleaseNoteAsync();
		await this.getUnreadNewsCountAsync();
		await useMembershipService().getRelatedAgenciesAsync();

		this.self.isFullyLoaded();

		// #region OnBoarding
		if (this.self.isTempWorker() && this.self.isConnected) {
			const agencyCompanyCode = JSON.parse(localStorage.getItem('agency-company-code') ?? '{}');
			// const hasAgencies = !!useMembershipService().relatedAgencies.length;

			if (this.route.query.agencyCompanyCode || (agencyCompanyCode.code && new Date() < new Date(agencyCompanyCode.expirationDate))) {
				const code = this.route.query.agencyCompanyCode ?? agencyCompanyCode.code;
				useMembershipService().openTempWorkerAddMembershipModalAsync(code);
			}

			if (agencyCompanyCode.code && new Date() > new Date(agencyCompanyCode.expirationDate)) {
				localStorage.removeItem('agency-company-code');
			}
		}
		// #endregion

		// Widget de support Zendesk : Agences connectées uniquement. Chargement non bloquant
		if (this.self.isAgency() && this.self.isConnected) {
			useZendeskService().loadAsync().catch(() => {});
		}

		useLoader().hide();
	}

	toggleLeftAside (open?: boolean) {
		this.ui.setAsideLeftSlideOut(open ?? !this.ui.asideLeftSlideOut);
	}

	private toggleRightAside (open?: boolean) {
		this.ui.setAsideRightSlideOut(open ?? !this.ui.asideRightSlideOut);
	}

	private async getUnreadNewsCountAsync () {
		if (this.self.isAgency()) return;
		this.state.unreadNewsCount = await useUserNewsService().countNotReadAsync();
	}

	private async checkReleaseNoteAsync () {
		// if (this.self.isTempWorker()) return;

		const noRelease = await this.self.getUserSettingAsync(SETTING_TYPE.ReleaseNote);

		if (noRelease === false) {
			useModal({
				Nested: defineAsyncComponent(() => import('@/components/modal/ModalReleaseNote.vue')),
				hideClose: true,
				hideOnOverlayClick: false,
				hideOnEsc: false,
			});
		}
	}

	private showAdminNotification (model: IBroadcastModel) {
		if (this.notif[model.id]) {
			this.notif[model.id].close();
		}

		const options = {
			duration: null,
			title: model.title,
			message: model.message,
			icon: model.icon ? model.icon as Orion.Icon : 'triangle_warning',
		};

		if (Object.keys(useNotif).includes(model.color as any)) {
			const color = model.color as keyof typeof useNotif;

			this.notif[model.id] = useNotif[color](options);
		}
		else {
			this.notif[model.id] = useNotif.warning(options);
		}
	}

	private deleteAdminNotification (id: number) {
		if (this.notif[id]) {
			this.notif[id].close();
		}
	}

	async handlePopoverShowAsync () {
		this.state.loading = true;
		this.state.showPermissionTooltip = true;
		this.state.roles = OrganizationRoleListItemEntity.map(await useOrganizationRoleService().selfAsync());
		this.state.tags = await useTagUserService().selfAsync();
		this.state.loading = false;
	}

	handlePopoverHide () {
		this.state.showPermissionTooltip = false;
	}

	openUserRolesAside () {
		this.state.showPermissionTooltip = false;
		useAside({
			Nested: defineAsyncComponent(() => import('@/components/organization-user/OrganizationUserAsideDetails.vue')),
			NestedProps: {
				id: this.self.organizationUserId,
				readonly: true,
				self: true,
			},
			size: 'lg',
		});
	}

	showUserProfile () {
		if (this.self.suiteUserId)
			window.location.href = SUITE_URL + `/profil/general?source=Armado&sourceUrl=${location.href}`;
		else {
			this.router.push({ name: 'UserProfileInformations' });
		}
	}

}
